import React, { useState } from "react";
import { Box, Link, Stack, Theme, Typography } from "@mui/joy";

import { convertReferencesToMarkdown, ReferenceProcessor } from "../util/references";
import Feedback from "./Feedback";
import { MarvinSenderRow } from "./MarvinSenderRow";
import RelatedFragmentNode from "./nodes/RelatedFragmentNode";
import { MarkdownRenderer } from "./MarkdownRenderer";
import { MessageProps } from "../shared/props";

// To prevent UI being unresponsive we limit the maximum. This is mainly around legacy very large tables.
const AUTO_RENDER_MAX_MESSAGE_LENGTH = 16 * 1024;

export default function MarkdownMessage(props: MessageProps) {
    const { setCurrentFileViewerFile, currentFileViewerFile, message: msg } = props;
    const isMarvin = msg.user.name === "Marvin";

    const [overrideRenderMaxLength, setOverrideRenderMaxLength] = useState(false);

    const messageKey = "message-" + msg.id;

    const messageContainerStyle = (theme: Theme) => ({
        borderRadius: theme.radius.md,
        padding: 2,
        width: isMarvin ? "100%" : "auto",
        alignSelf: isMarvin || props.singleMessageView ? "flex-start" : "flex-end",
        backgroundColor:
            isMarvin || props.singleMessageView ? "white" : "var(--joy-palette-neutral-100)",
    });

    const referenceProcessor = new ReferenceProcessor(msg);

    // Trim the message if it's too long
    let messageToRender = convertReferencesToMarkdown(msg.message);
    const messageTooLong = messageToRender.length > AUTO_RENDER_MAX_MESSAGE_LENGTH;
    const showEntireMessage = !messageTooLong || overrideRenderMaxLength;
    if (!showEntireMessage) {
        messageToRender = messageToRender.slice(0, AUTO_RENDER_MAX_MESSAGE_LENGTH);
    }

    return (
        <Box key={"container" + messageKey} sx={messageContainerStyle}>
            <Stack direction="column" spacing={1}>
                {isMarvin && !props.singleMessageView && <MarvinSenderRow />}

                <MarkdownRenderer
                    message={messageToRender}
                    contentId={msg.id}
                    showTableDownloadCopyButtons={showEntireMessage}
                    processLinks={isMarvin}
                    currentFileViewerFile={currentFileViewerFile}
                    setCurrentFileViewerFile={setCurrentFileViewerFile}
                    referenceProcessor={referenceProcessor}
                />

                {!showEntireMessage && (
                    <Typography level="body-xs">
                        This response is very large ({Math.round(msg.message.length / 1024)}
                        kb!), so I'm only showing you part of it. You can see the whole message, but
                        things may get a little slow.
                        <Link
                            color="neutral"
                            variant="soft"
                            onClick={() => setOverrideRenderMaxLength(true)}
                        >
                            Show me the whole thing!
                        </Link>
                    </Typography>
                )}

                {msg.fragmentNode && <RelatedFragmentNode node={msg.fragmentNode} />}
                {isMarvin && !props.singleMessageView && <Feedback messageId={msg.id} />}
            </Stack>
        </Box>
    );
}
